import { Vue, Component } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import LORFacetsEngine from './lor-facets-engine';
import LORFacetsMetadataCard from '@/components/lor-facets-metadata-card/lor-facets-metadata-card';
import LORFacetContentItem from '@/components/lor-facet-content-item/lor-facet-content-item';
import draggable from 'vuedraggable';
import { adminUtilsAPI } from '@/providers/apis/admin-utils/admin-utils';
import VueMultiSelect from 'vue-multiselect';
import { FacetsFilterKeyModel } from '@/models/facets/facets';
import { sessionService } from '@/providers/services/auth/session';

@Component({
    name: 'lor-facets',
    components: {
        'material-icons': GoogleMaterialIcon,
        'facet-metadata-card': LORFacetsMetadataCard,
        'facet-content-card': LORFacetContentItem,
        draggable,
        'vue-multi-select': VueMultiSelect,
    },
})

export default class LORFacets extends Vue {

    public get filteredList() {
        const facetsList = this.facetsList;
        const selectedFacet = this.selectedFacet;
        const searchTerms = this.searchTerms;
        const activeFact = selectedFacet ? facetsList[selectedFacet] : null;
        const activeFacetItem = activeFact ? activeFact.items : [];
        return activeFacetItem.filter(
            (item: any) => (item[activeFact.filterKey.labelKey].toLowerCase())
                .indexOf(searchTerms.toLowerCase()) !== -1);
    }

    public get session() {
        return sessionService.getSession();
    }

    // ---------------------------------------------------
    // Properties

    public selectedFacet: string | any = 'content-type';

    public isLoading: boolean = false;

    public isFacetEdit: boolean = false;

    public searchTerms: string = '';

    public tenantsList: any = [];

    public selectedTenant: any = null;

    /**
     * @Property facetsList has contain the list of facets with schema
     * @note Don't change the facetKey name it is used to call the API
     */
    public facetsList: any = {};

    public sortingOrders: any = [];

    public isEditMode: boolean = false;

    public editFacetItem: any = null;

    public facetKeysList: any = [];

    private lorFacetsEngine: LORFacetsEngine = new LORFacetsEngine();

    private offset: number = 0;

    private limit: number = 20;

    private tenantDataCount: number = 0;

    // ---------------------------------------------------
    // Hooks

    public created() {
        const params: any = {
            offset: 0,
            limit: this.limit,
        };
        this.fetchTenantList(params);
        this.fetchFacetsList();
    }

    public mounted() {
        this.scrollEventAttach();
    }

    // ---------------------------------------------------
    // Actions

    public onSelectFacet(filterKey: FacetsFilterKeyModel) {
        this.selectedFacet = filterKey.facetKey;
        this.loadFacetItems(filterKey);
    }

    public onSelectItems(item: any) {
       this.updateItems(item);
    }

    public onDeleteFacetItem(item: any) {
        const facetsList = this.facetsList;
        const selectedFacet = this.selectedFacet;
        const activeFacet = facetsList[selectedFacet];
        const activeIndex = activeFacet.items.indexOf(item);
        const { filterKey, selectedItems = [] } = activeFacet;
        const selectedIndex = selectedItems.indexOf(item[filterKey.valueKey]);
        if (selectedIndex !== -1) {
            selectedItems.splice(selectedIndex, 1);
        }
        activeFacet.items.splice(activeIndex, 1);
    }

    public onEditFacetItem(item: any) {
        this.editFacetItem = item;
    }

    public onHideFactBar(hideAction: any) {
        this.editFacetItem = null;
        hideAction();
    }

    public updateItems(item: any) {
        const facetsList = this.facetsList;
        const selectedFacet = this.selectedFacet;
        const activeFacet = facetsList[selectedFacet];
        const { filterKey } = activeFacet;
        const selectedItems = activeFacet.selectedItems;
        const activeIndex = selectedItems.indexOf(item[filterKey.valueKey]);
        activeIndex !== -1 ? selectedItems.splice(activeIndex, 1) : selectedItems.push(item[filterKey.valueKey]);
    }

    public onAddMetadata() {
        this.isFacetEdit = false;
        (this.$refs['facet-metadata'] as any).show();
    }

    public onCreateFacet(newFacet: any) {
        this.facetsList = {...this.facetsList, ...newFacet};
        this.parseSortingOrder(this.facetsList);
        this.showSuccessToaster('Added facet metadata into settings', 'Updated Settings');
        this.onSaveFacets();
    }

    public onDeleteMetadata(facetKey: string) {
        delete this.facetsList[facetKey];
        this.parseSortingOrder(this.facetsList);
        (this.$refs['facet-metadata'] as any).hide();
        this.onSaveFacets();
    }

    public onSaveFacets() {
        this.isLoading = true;
        this.orderingFacets();
        const tenantId = this.session!.tenant!.tenant_id;
        const { id = tenantId } = this.selectedTenant || {};
        this.lorFacetsEngine.createFacetsList(this.facetsList, id).then(() => {
            this.fetchFacetsList();
            this.isLoading = false;
            this.showSuccessToaster('Updated the facets settings successfully!.', 'Successfully Updated');
        });
    }

    public onEditFacet(key: string) {
        this.selectedFacet = key;
        this.isFacetEdit = true;
        (this.$refs['facet-metadata'] as any).show();
    }

    public onSwitchMode() {
        this.isEditMode = !this.isEditMode;
    }

    public onSelectTenant(selectedItem: any) {
        this.selectedTenant = selectedItem;
        this.fetchFacetsList();
    }

    // --------------------------------------------------
    // Methods

    public fetchFacetsList() {
        this.isLoading = true;
        const selectedTenant = this.selectedTenant;
        const tenantId = selectedTenant ? selectedTenant.id : null;
        this.lorFacetsEngine.getFacetsList(tenantId).then(async (facets) => {
            this.facetsList = facets;
            await this.parseSortingOrder(facets);

            // NOTE: --------- Help to highlight existing facet -----------
            const selectedFacet  = this.selectedFacet;
            if (selectedFacet) {
                const activeFacet = this.sortingOrders.find((sortItem: any) => sortItem.facetKey === selectedFacet);
                if (activeFacet) {
                    this.onSelectFacet(activeFacet.filterKey);
                }
            }
            this.isLoading = false;
        });
    }

    private async parseSortingOrder(facetsList: any) {
        const facetsKeyNames = Object.keys(facetsList);
        const sortingOrders: any = [];
        this.facetKeysList = facetsKeyNames;
        facetsKeyNames.forEach((facetKeyName, index) => {
            const activeFacet = facetsList[facetKeyName];
            if (activeFacet) {
                const createObj = {
                    name: activeFacet.name,
                    order: index + 1,
                    fixed: facetKeyName === 'system',
                    facetKey: facetKeyName,
                    filterKey: activeFacet.filterKey,
                };
                sortingOrders.push(createObj);
            }
        });
        this.sortingOrders = sortingOrders;
    }

    private orderingFacets() {
        const sortingOrders = this.sortingOrders;
        const facetsSorting: any = {};
        sortingOrders.forEach((sortItem: any) => {
            facetsSorting[sortItem.facetKey] = this.facetsList[sortItem.facetKey];
        });
        this.facetsList = facetsSorting;
    }

    private fetchTenantList(params: any) {
        if (this.tenantsList.length !== 0 && this.offset >= this.tenantDataCount) {
            return;
        }
        adminUtilsAPI.fetchTenantList(params).then((tenantsList) => {
            this.tenantsList = this.tenantsList.concat(tenantsList.list);
            this.tenantDataCount = tenantsList.total_count;
        });
    }

    private showSuccessToaster(description: string, heading: string) {
        this.$bvToast.toast(description, {
            title: heading,
            variant: 'success',
            solid: true,
        });
    }

    private loadFacetItems(filterKey: FacetsFilterKeyModel) {
        const lorFacetsEngine = this.lorFacetsEngine;
        this.isLoading = true;
        const key: any = filterKey.facetKey;
        if (lorFacetsEngine.actionMapping[key]) {
            lorFacetsEngine.actionMapping[key]().then((contentData: any) => {
                this.facetsList[key].items = contentData;
                this.facetsList[key].isApiData = true;
                this.isLoading = false;
            });
        } else {
            lorFacetsEngine.fetchMetadataDetails(filterKey).then((facetData) => {
                if (facetData && facetData.length) {
                    this.facetsList[key].items = facetData;
                    this.facetsList[key].isApiData = true;
                }
                this.isLoading = false;
            });
        }
    }

    private onScroll() {
        this.offset += this.limit;
        const params: any = {
            offset: this.offset,
            limit: this.limit,
        };
        this.fetchTenantList(params);
    }

    private scrollEventAttach() {
        const tenantDd: any = document.querySelector('#facetsTenantMS .multiselect__content-wrapper');
        if (tenantDd) {
            tenantDd.addEventListener('scroll', (event: any) => {
                const {scrollHeight, scrollTop, clientHeight} = event.target;
                if (Math.abs(scrollHeight - clientHeight - scrollTop) < 1) {
                    this.onScroll();
                }
            });
        }
    }

}
